import { Injectable } from '@angular/core';
import { forkJoin, map, switchMap, of, type Observable } from 'rxjs';
import { BaseApiService } from '../../../core/api/base-api.service';
import type { ListCrmContactsResponse } from '../../../core/api/generated/models/ListCrmContactsResponse';
import type { LeadDetail, LeadListResponse } from './leads.types';

export interface CreatedContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

export interface ContactSearchResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
}

export interface CreateLeadInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

@Injectable({ providedIn: 'root' })
export class LeadsApiService extends BaseApiService {
  protected override readonly resourcePath = 'v1/revenue';

  listLeads(query: { has_active_sale?: boolean; page?: number; pageSize?: number } = {}) {
    return this.get<LeadListResponse>('leads', { params: { pageSize: 50, ...query } });
  }

  getLead(contactId: string) {
    return this.get<LeadDetail>(['leads', contactId]);
  }

  /**
   * Server-side search on CRM contacts. Returns any contact regardless of
   * whether they already have a sale — used by the /vendas/criar wizard so
   * the user can vincular qualquer contato existente (inclusive primeira
   * venda do contato).
   */
  searchContacts(search: string, pageSize = 20): Observable<ContactSearchResult[]> {
    const params: Record<string, string | number> = { pageSize };
    const term = search.trim();
    if (term) params['search'] = term;
    return this.get<ListCrmContactsResponse>('/v1/crm/contacts', { params }).pipe(
      map((resp) =>
        (resp.items ?? []).map((c): ContactSearchResult => ({
          id: c.id,
          firstName: c.firstName ?? null,
          lastName: c.lastName ?? null,
          status: c.status,
        })),
      ),
    );
  }

  /**
   * Cross-domain helper: creates a CRM contact and attaches the provided
   * channel identities. Returns the created contact's id ready to be linked
   * to a Sale. Used by the /vendas/criar wizard.
   */
  createLead(input: CreateLeadInput): Observable<CreatedContact> {
    const contactBody: Record<string, string> = { origin: 'manual' };
    if (input.firstName?.trim()) contactBody['firstName'] = input.firstName.trim();
    if (input.lastName?.trim()) contactBody['lastName'] = input.lastName.trim();

    return this.post<CreatedContact, typeof contactBody>('/v1/crm/contacts', contactBody).pipe(
      switchMap((contact) => {
        const identityRequests: Observable<unknown>[] = [];
        if (input.email?.trim()) {
          identityRequests.push(
            this.post<unknown, { type: string; value: string; isPrimary: boolean }>(
              `/v1/crm/contacts/${contact.id}/identities`,
              { type: 'email', value: input.email.trim().toLowerCase(), isPrimary: true },
            ),
          );
        }
        if (input.phone?.trim()) {
          identityRequests.push(
            this.post<unknown, { type: string; value: string; isPrimary: boolean }>(
              `/v1/crm/contacts/${contact.id}/identities`,
              { type: 'phone', value: input.phone.trim(), isPrimary: !input.email?.trim() },
            ),
          );
        }
        return identityRequests.length > 0
          ? forkJoin(identityRequests).pipe(map(() => contact))
          : of(contact);
      }),
    );
  }
}
